const DIRECT_URL_PATTERN = /^(blob:|data:|https?:|\/)/i;

const AUDIO_EXTENSIONS = new Set(["aac", "flac", "m4a", "mp3", "ogg", "wav"]);
const VIDEO_EXTENSIONS = new Set(["m4v", "mov", "mp4", "ogv", "webm"]);

const joinUrl = (baseUrl, path) => {
  const base = String(baseUrl || "").replace(/\/+$/, "");
  const suffix = String(path || "").replace(/^\/+/, "");

  return base && suffix ? `${base}/${suffix}` : "";
};

const cleanExtension = (value) => {
  const cleanValue = String(value || "")
    .split(/[?#]/)[0]
    .replace(/\/+$/, "");

  const lastSegment = cleanValue.split("/").pop() || "";
  return lastSegment.includes(".")
    ? lastSegment.split(".").pop().toLowerCase()
    : "";
};

export const resolvePublicNftUrl = (value, ipfsImageUrl = "") => {
  const source = String(value || "").trim();
  if (!source) return "";
  if (/^(?:ipfs:\/\/|\/?ipfs\/)/i.test(source)) {
    const normalizedSource = source
      .replace(/^ipfs:\/\//i, "")
      .replace(/^\/?ipfs\//i, "");

    return joinUrl(ipfsImageUrl, normalizedSource);
  }
  if (DIRECT_URL_PATTERN.test(source)) return source;

  return joinUrl(ipfsImageUrl, source);
};

export const deriveCanonicalAssetUrl = (tokenUri, variant = "original") => {
  const source = String(tokenUri || "").trim();
  if (!/^https?:\/\//i.test(source)) return "";

  try {
    const url = new URL(source);
    const match = url.pathname.match(/^\/metadata\/([^/]+)\/([^/]+)\.json$/i);
    if (!match) return "";

    url.pathname = `/assets/${match[1]}/${match[2]}/${variant}`;
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return "";
  }
};

export const getNftMediaKind = (item, metadata = {}) => {
  const contentType = String(
    item?.mediaContentType || item?.contentType || metadata?.content_type || "",
  ).toLowerCase();

  if (contentType.startsWith("audio/")) return "audio";
  if (contentType.startsWith("video/")) return "video";
  if (contentType === "application/pdf") return "book";
  if (contentType.startsWith("image/")) return "image";

  const candidates = [
    item?.image,
    item?.animationUrl,
    metadata?.animation_url,
    item?.additionalImage,
    item?.ipfsimage,
  ];

  for (const candidate of candidates) {
    const extension = cleanExtension(candidate);
    if (AUDIO_EXTENSIONS.has(extension)) return "audio";
    if (VIDEO_EXTENSIONS.has(extension)) return "video";
    if (extension === "pdf") return "book";
  }

  return "image";
};

export const resolveNftBackgroundUrl = (
  item,
  { backUrl = "", ipfsImageUrl = "" } = {},
) => {
  const creator = String(item?.tokenCreator || "").trim();
  const originalThumb = String(item?.thumb || "").trim();

  if (originalThumb) {
    if (DIRECT_URL_PATTERN.test(originalThumb)) return originalThumb;

    return joinUrl(
      backUrl,
      `Thumb_compressedImage/${creator}/${originalThumb}`,
    );
  }

  const compressedThumb = String(item?.thumb_additionalImage || "").trim();
  if (compressedThumb) {
    if (DIRECT_URL_PATTERN.test(compressedThumb)) return compressedThumb;

    return joinUrl(backUrl, `Thumb_nftImg/${creator}/${compressedThumb}`);
  }

  const ipfsThumb = String(item?.thumb_ipfs || "").trim();
  if (!ipfsThumb) return "";
  if (/^(blob:|data:|https?:)/i.test(ipfsThumb)) return ipfsThumb;

  const normalizedThumb = ipfsThumb
    .replace(/^ipfs:\/\//i, "")
    .replace(/^\/?ipfs\//i, "");

  return joinUrl(ipfsImageUrl, normalizedThumb);
};

export const resolveNftMedia = (
  item,
  {
    backUrl = "",
    ipfsImageUrl = "",
    metadata = {},
  } = {},
) => {
  const creator = String(item?.tokenCreator || "").trim();
  const kind = getNftMediaKind(item, metadata);
  const tokenUri = resolvePublicNftUrl(item?.tokenUri, ipfsImageUrl);
  const animationUrl = resolvePublicNftUrl(
    item?.animationUrl || metadata?.animation_url,
    ipfsImageUrl,
  );
  const canonicalOriginalUrl = deriveCanonicalAssetUrl(tokenUri, "original");
  const storedMediaUrl = resolvePublicNftUrl(item?.mediaUrl, ipfsImageUrl);
  const storedIpfsUrl = resolvePublicNftUrl(item?.ipfsimage, ipfsImageUrl);
  const metadataImageUrl = resolvePublicNftUrl(metadata?.image, ipfsImageUrl);
  const canonicalThumbnailUrl = deriveCanonicalAssetUrl(tokenUri, "thumbnail");
  const backgroundUrl = resolveNftBackgroundUrl(item, {
    backUrl,
    ipfsImageUrl,
  });
  const rawImage = String(item?.image || "").trim();
  const directImageUrl = DIRECT_URL_PATTERN.test(rawImage)
    ? resolvePublicNftUrl(rawImage, ipfsImageUrl)
    : "";
  const legacyOriginalUrl = directImageUrl || (
    creator && item?.image
      ? joinUrl(backUrl, `compressedImage/${creator}/${item.image}`)
      : ""
  );

  const coverUrl = metadataImageUrl
    || storedMediaUrl
    || storedIpfsUrl
    || canonicalThumbnailUrl
    || backgroundUrl;

  const originalUrl = kind === "image"
    ? storedMediaUrl || storedIpfsUrl || metadataImageUrl || legacyOriginalUrl
    : animationUrl
      || canonicalOriginalUrl
      || legacyOriginalUrl
      || storedIpfsUrl;

  return {
    kind,
    tokenUri,
    coverUrl,
    originalUrl,
    animationUrl: animationUrl || (kind === "image" ? "" : originalUrl),
  };
};
